import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc.js'
import semver from 'semver'
import { propertyRequirementsVersion } from './rules.js'

dayjs.extend(utc)

const checkExpired = (tag) => {
  const rowDate = tag.split('expiresAt_')[1]
  const date = dayjs.utc(rowDate)
  if (!date.isValid()) return false
  const expired = date.isBefore(dayjs())
  return !expired
}

const checkIsSupportedJurisdiction = (tag, countryCode) => {
  const jurisdictions = tag.split('jurisdictions_')[1]
  if (!jurisdictions) return false

  return jurisdictions.split('_').includes(countryCode)
}

const semverCache = new Map()
const semverSatisfies = (version, range) => {
  if (typeof version !== 'string' || typeof range !== 'string') return false
  if (!version.includes('.')) return false // doesn't look like a version
  if (!/[\d*|]/u.test(range) && !['x', ''].includes(range.trim())) return false // '', '||', 'x <5' are valid
  const key = JSON.stringify([version, range])
  if (!semverCache.has(key)) semverCache.set(key, semver.satisfies(version, range))
  return semverCache.get(key)
}

const checkAnnouncementPropsVersionIsUnsupported = (announcement, filterVersion) => {
  const requirement = propertyRequirementsVersion(announcement)
  return requirement && !semverSatisfies(filterVersion, requirement)
}

const conditionApplies = ({ condition, filters }) => {
  if (typeof condition !== 'string' || condition.length === 0) return false

  condition = condition.trim()

  const matchesCondition = (condition) => {
    if (condition === filters.os) return true

    if (condition === filters.platform) return true

    if (condition === 'dev' && filters.build === 'development') return true

    if (semverSatisfies(filters.version, condition)) return true

    if (condition.startsWith('expiresAt_')) return checkExpired(condition)

    if (condition.startsWith('osVersion_')) {
      return semverSatisfies(filters.osVersion, condition.split('osVersion_')[1])
    }

    if (condition === 'noBackup') return !filters.hasBackup && filters.hasBalance

    if (condition.startsWith('jurisdictions_')) {
      return checkIsSupportedJurisdiction(condition, filters.countryCode)
    }

    if (condition.startsWith('trezor_') || condition.startsWith('ledger_')) {
      const walletAccounts = filters.walletAccounts || []
      const [walletSource, ...rest] = condition.split('_')
      const subCondition = rest.join('_')

      const filteredWalletAccounts = walletAccounts.filter(
        (w) => w.enabled && w.source === walletSource
      )
      if (filteredWalletAccounts.length === 0) return false

      if (subCondition === 'hasWalletAccounts') return true

      const assetBalanceMatch = subCondition.match(/^(\w+)Balance$/u)
      if (assetBalanceMatch) {
        const assetName = assetBalanceMatch[1]
        return filteredWalletAccounts.some((acc) => {
          const accountBalances = filters.balances[acc.id]
          if (!accountBalances || !accountBalances[assetName]) return false
          return accountBalances[assetName].balance && !accountBalances[assetName].balance.isZero
        })
      }

      const assetHistoryMatch = subCondition.match(/^(\w+)History$/u)
      if (assetHistoryMatch) {
        const assetName = assetHistoryMatch[1]
        return filteredWalletAccounts.some((acc) => {
          const accountTxLogs = filters.txLogs[acc.id]
          if (!accountTxLogs || !accountTxLogs[assetName]) return false
          return accountTxLogs[assetName].size > 0
        })
      }

      return false
    }

    if (condition === 'hasBalance') return filters.hasBalance

    const assetBalance = condition.match(/(\w+)Balance/u)
    if (assetBalance) {
      const assetName = assetBalance[1]
      return Object.values(filters.balances).some(
        (v) => v[assetName] && !v[assetName].balance.isZero
      )
    }

    const assetHistory = condition.match(/(\w+)History/u)
    if (assetHistory) {
      const assetName = assetHistory[1]
      return Object.values(filters.txLogs).some((v) => v[assetName] && v[assetName].size > 0)
    }

    // hasNftsStored is legacy desktop name
    if (condition === 'hasNfts' || condition === 'hasNftsStored') return filters.hasNfts

    if (condition === 'hasSeedlessBackup') return filters.hasSeedlessBackup

    const assetStaked = condition.match(/(\w+)Staked/u)
    if (assetStaked) {
      const assetName = assetStaked[1]
      return (
        filters.hasBalance &&
        Object.values(filters.balances).some((v) => v[assetName] && !!v[assetName].staked) // `staked` is likely a NumberUnit not a boolean
      )
    }

    if (condition === 'hasWalletAccounts') {
      const walletAccounts = filters.walletAccounts || []
      return walletAccounts.some((w) => w.enabled)
    }

    return false
  }

  return condition[0] === '!' ? !matchesCondition(condition.slice(1)) : matchesCondition(condition)
}

const filterAnnouncement = ({ announcement, filters }) => {
  if (!filters.shownAnnouncements) return false

  if (filters.shownAnnouncements[announcement.id]) return false

  if (checkAnnouncementPropsVersionIsUnsupported(announcement, filters.version)) return false

  if (!Array.isArray(announcement.conditions)) return false

  return announcement.conditions.every((condition) => conditionApplies({ condition, filters }))
}

const filterAnnouncements = ({ announcements, filters }) => {
  return announcements.filter((announcement) => filterAnnouncement({ announcement, filters }))
}

export default filterAnnouncements
